import ImportedSourceData from '../components/ImportedSourceData';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Pagination,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Collapse,
  Switch,
  FormControlLabel,
  Drawer,
  useMediaQuery,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Computer as ComputerIcon,
  DesktopWindows as DesktopWindowsIcon,
  Key as KeyIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  FilterAlt as FilterAltIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Sort as SortIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Print as PrintIcon,
  Monitor as MonitorIcon,
  Headset as HeadsetIcon,
  Phone as PhoneIcon,
  Router as RouterIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { assetService } from '../services/api';
import Layout from '../components/Layout/Layout';
import { AssetListSkeleton, LoadingOverlay } from '../components/common/LoadingSkeleton';
import { FadeIn, StaggeredFadeIn, SlideIn } from '../components/common/Animations';

// Importar constantes compartilhadas
import {
  ASSET_CATEGORIES,
  PHYSICAL_SUBCATEGORIES,
  ASSET_STATUSES,
  getCategoryInfo,
  getSubcategoryInfo,
  getStatusInfo
} from '../constants/assetConstants';

// Componente melhorado para cards de ativos físicos
const PhysicalAssetCard = ({ asset, onEdit, onDelete }) => {
  const subcategoryInfo = getSubcategoryInfo(asset.subcategory);
  const canEdit = asset.can_edit !== false;

  const getSubcategoryIcon = (subcategory) => {
    const icons = {
      'Computadores': <DesktopWindowsIcon />,
      'Monitores': <MonitorIcon />,
      'Impressoras': <PrintIcon />,
      'Periféricos': <HeadsetIcon />,
      'Telefonia': <PhoneIcon />,
      'Servidores': <RouterIcon />,
      'Consumíveis': <InventoryIcon />,
      'Outro': <BuildIcon />
    };
    return icons[subcategory] || <DesktopWindowsIcon />;
  };

  return (
    <Card sx={{ height: '100%', position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            {getSubcategoryIcon(asset.subcategory)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom>
              {asset.name}
            </Typography>
                          <ImportedSourceData product={asset} />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={asset.subcategory}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label={ASSET_STATUSES[asset.status]?.label || asset.status}
                color={ASSET_STATUSES[asset.status]?.color || 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {asset.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {asset.description.length > 100
              ? `${asset.description.substring(0, 100)}...`
              : asset.description
            }
          </Typography>
        )}

        {asset.category === 'physical' && <Typography variant="body2">Quantidade: {asset.quantity ?? 1}</Typography>}
        {/* Campos específicos baseados na subcategoria */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {asset.owner && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">{asset.owner}</Typography>
            </Box>
          )}

          {asset.location && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">{asset.location}</Typography>
            </Box>
          )}

          {asset.value && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                R$ {asset.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}

          {asset.subcategory === 'Telefonia' && (
            <>
              {asset.phone_number && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.phone_number}</Typography>
                </Box>
              )}
              {(asset.phone_user || asset.carrier || asset.phone_plan || asset.iccid || asset.phone_situation) && (
                <Stack spacing={0.5}>
                  {asset.phone_user && <Typography variant="caption">Usuário: {asset.phone_user}</Typography>}
                  {asset.carrier && <Typography variant="caption">Operadora: {asset.carrier}</Typography>}
                  {asset.phone_plan && <Typography variant="caption">Plano: {asset.phone_plan}</Typography>}
                  {asset.iccid && <Typography variant="caption">ICCID: {asset.iccid}</Typography>}
                  {asset.phone_situation && <Typography variant="caption">Situação: {asset.phone_situation}</Typography>}
                </Stack>
              )}
            </>
          )}

          {/* Campos específicos por subcategoria */}
          {asset.subcategory === 'Computadores' && (
            <>
              {asset.computer_type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ComputerIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.computer_type}</Typography>
                </Box>
              )}
              {asset.memory && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MemoryIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.memory} GB RAM</Typography>
                </Box>
              )}
              {asset.storage && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.storage}</Typography>
                </Box>
              )}
            </>
          )}

          {asset.subcategory === 'Monitores' && (
            <>
              {asset.monitor_type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonitorIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.monitor_type}</Typography>
                </Box>
              )}
              {asset.size && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonitorIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.size}"</Typography>
                </Box>
              )}
            </>
          )}

          {asset.subcategory === 'Impressoras' && (
            <>
              {asset.printer_type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PrintIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.printer_type}</Typography>
                </Box>
              )}
              {asset.current_page_counter && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PrintIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">Páginas: {asset.current_page_counter.toLocaleString('pt-BR')}</Typography>
                </Box>
              )}
            </>
          )}

          {asset.subcategory === 'Servidores' && (
            <>
              {asset.server_type && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RouterIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.server_type}</Typography>
                </Box>
              )}
              {asset.memory && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MemoryIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{asset.memory} GB RAM</Typography>
                </Box>
              )}
            </>
          )}

          {/* Campos de controle para ativos físicos */}
          {asset.category === 'physical' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={asset.in_stock ? 'Em estoque' : 'Fora de estoque'}
                  size="small"
                  color={asset.in_stock ? 'success' : 'warning'}
                  variant="outlined"
                />
                <Chip
                  label={asset.with_user ? 'Com usuário' : 'Sem usuário'}
                  size="small"
                  color={asset.with_user ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
            </>
          )}

          {asset.serial_number && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                SN: {asset.serial_number}
              </Typography>
            </Box>
          )}

          {asset.inventory_number && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                INV: {asset.inventory_number}
              </Typography>
            </Box>
          )}

          {asset.permission_groups?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {asset.permission_groups.slice(0, 3).map((group) => (
                <Chip key={group.id} label={group.name} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Botões de ação */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0 }}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={onEdit} color="primary" disabled={!canEdit}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton size="small" onClick={onDelete} color="error" disabled={!canEdit}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

// Componente para cards de outras categorias
const GenericAssetCard = ({ asset, onEdit, onDelete }) => {
  const categoryInfo = getCategoryInfo(asset.category);
  const canEdit = asset.can_edit !== false;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: `${categoryInfo.color}.main`, mr: 2 }}>
            {categoryInfo.icon === 'computer' && <ComputerIcon />}
            {categoryInfo.icon === 'key' && <KeyIcon />}
            {categoryInfo.icon === 'assignment' && <AssignmentIcon />}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom>
              {asset.name}
            </Typography>
                          <ImportedSourceData product={asset} />
            <Chip
              label={ASSET_STATUSES[asset.status]?.label || asset.status}
              color={ASSET_STATUSES[asset.status]?.color || 'default'}
              size="small"
            />
          </Box>
        </Box>

        {asset.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {asset.description.length > 100
              ? `${asset.description.substring(0, 100)}...`
              : asset.description
            }
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {asset.owner && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">{asset.owner}</Typography>
            </Box>
          )}

          {asset.location && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">{asset.location}</Typography>
            </Box>
          )}

          {asset.value && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                R$ {asset.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}

          {/* Campos específicos por categoria */}
          {asset.category === 'token' && asset.token_value && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <KeyIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {asset.token_value.length > 30
                  ? `${asset.token_value.substring(0, 30)}...`
                  : asset.token_value
                }
              </Typography>
            </Box>
          )}

          {asset.category === 'token' && (asset.token_service || asset.token_environment || asset.token_project) && (
            <Stack spacing={0.5}>
              {asset.token_service && <Typography variant="caption">Serviço: {asset.token_service}</Typography>}
              {asset.token_environment && <Typography variant="caption">Ambiente: {asset.token_environment}</Typography>}
              {asset.token_project && <Typography variant="caption">Projeto: {asset.token_project}</Typography>}
            </Stack>
          )}

          {asset.category === 'license' && asset.expiry_date && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2">
                Expira: {new Date(asset.expiry_date).toLocaleDateString('pt-BR')}
              </Typography>
            </Box>
          )}

          {asset.permission_groups?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {asset.permission_groups.slice(0, 3).map((group) => (
                <Chip key={group.id} label={group.name} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 0 }}>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={onEdit} color="primary" disabled={!canEdit}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton size="small" onClick={onDelete} color="error" disabled={!canEdit}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

// Componente unificado para cards
const AssetCard = ({ asset, onEdit, onDelete }) => {
  if (asset.category === 'physical') {
    return <PhysicalAssetCard asset={asset} onEdit={onEdit} onDelete={onDelete} />;
  }
  return <GenericAssetCard asset={asset} onEdit={onEdit} onDelete={onDelete} />;
};

const AssetList = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estados principais
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Estados de visualização
  const [viewMode, setViewMode] = useState('cards');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Estados de seleção
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const categoryInfo = getCategoryInfo(category);
  const physicalSubcategories = Object.keys(PHYSICAL_SUBCATEGORIES);

  // Gerenciar parâmetros da URL e filtros
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);
  const previousFilters = useRef({});

  useEffect(() => {
    // Sempre atualizar filtros quando parâmetros da URL mudarem
    const subcategoryParam = searchParams.get('subcategory');
    const statusParam = searchParams.get('status');
    const locationParam = searchParams.get('location');
    const ownerParam = searchParams.get('owner');

    // Atualizar filtros baseado nos parâmetros da URL
    if (subcategoryParam && category === 'physical') {
      setSubcategoryFilter(subcategoryParam);
    } else if (!subcategoryParam && category === 'physical') {
      setSubcategoryFilter('all');
    }

    if (statusParam) {
      setStatusFilter(statusParam);
    } else {
      setStatusFilter('all');
    }

    if (locationParam) {
      setLocationFilter(locationParam);
    } else {
      setLocationFilter('');
    }

    if (ownerParam) {
      setOwnerFilter(ownerParam);
    } else {
      setOwnerFilter('');
    }

    // Recarregar dados sempre que parâmetros mudarem
    loadAssets();
  }, [category, searchParams]);

  // Adicionar um useEffect específico para subcategoryFilter para garantir atualização
  useEffect(() => {
    if (category === 'physical') {
      loadAssets();
    }
  }, [subcategoryFilter]);

  // Função para atualizar URL quando filtros mudam (apenas se for mudança do usuário)
  const updateURL = (newFilters) => {
    const currentFilters = {
      subcategory: subcategoryFilter,
      status: statusFilter,
      location: locationFilter,
      owner: ownerFilter
    };

    // Só atualizar URL se os filtros realmente mudaram
    if (JSON.stringify(currentFilters) !== JSON.stringify(previousFilters.current)) {
      const params = new URLSearchParams();

      if (newFilters.subcategory && newFilters.subcategory !== 'all') {
        params.set('subcategory', newFilters.subcategory);
      }
      if (newFilters.status && newFilters.status !== 'all') {
        params.set('status', newFilters.status);
      }
      if (newFilters.location && newFilters.location.trim()) {
        params.set('location', newFilters.location);
      }
      if (newFilters.owner && newFilters.owner.trim()) {
        params.set('owner', newFilters.owner);
      }

      setSearchParams(params, { replace: true });
      previousFilters.current = { ...newFilters };
    }
  };

  // Atualizar URL quando filtros mudarem (apenas se não for inicialização)
  useEffect(() => {
    if (isInitialized.current) {
      updateURL({
        subcategory: subcategoryFilter,
        status: statusFilter,
        location: locationFilter,
        owner: ownerFilter
      });
    }
  }, [subcategoryFilter, statusFilter, locationFilter, ownerFilter]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [assets, searchTerm, statusFilter, subcategoryFilter, locationFilter, ownerFilter, sortBy, sortOrder]);

  const loadAssets = async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);

      // Passar subcategoria se estiver definida
      const subcategoryParam = subcategoryFilter !== 'all' ? subcategoryFilter : null;
      const response = await assetService.getAssetsByCategory(category, subcategoryParam);
      setAssets(response.assets || []);
    } catch (error) {
      console.error('Erro ao carregar ativos:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar ativos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...assets];

    // Filtros de texto
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(asset =>
        asset.name.toLowerCase().includes(searchLower) ||
        asset.description?.toLowerCase().includes(searchLower) ||
        asset.owner?.toLowerCase().includes(searchLower) ||
        asset.location?.toLowerCase().includes(searchLower) ||
        asset.serial_number?.toLowerCase().includes(searchLower) ||
        asset.inventory_number?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(asset => asset.status === statusFilter);
    }

    // Filtro por subcategoria (apenas para físicos)
    if (category === 'physical' && subcategoryFilter !== 'all') {
      filtered = filtered.filter(asset => asset.subcategory === subcategoryFilter);
    }

    // Filtro por localização
    if (locationFilter) {
      filtered = filtered.filter(asset =>
        asset.location?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    // Filtro por responsável
    if (ownerFilter) {
      filtered = filtered.filter(asset =>
        asset.owner?.toLowerCase().includes(ownerFilter.toLowerCase())
      );
    }

    // Ordenação
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Tratamento especial para campos
      if (sortBy === 'value') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else if (sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredAssets(filtered);
    setCurrentPage(1); // Reset para primeira página quando filtros mudam
  };

  const handleEdit = (assetId) => {
    // Usar rotas específicas por categoria
    if (category === 'physical') {
      navigate(`/assets/physical/${assetId}/edit`);
    } else if (category === 'token') {
      navigate(`/assets/token/${assetId}/edit`);
    } else if (category === 'license') {
      navigate(`/assets/license/${assetId}/edit`);
    } else {
      // Fallback para rota genérica
      navigate(`/assets/${assetId}/edit`);
    }
  };

  const handleDelete = async (assetId) => {
    if (window.confirm('Tem certeza que deseja excluir este ativo?')) {
      try {
        await assetService.deleteAsset(assetId, category);
        setSnackbar({
          open: true,
          message: 'Ativo excluído com sucesso',
          severity: 'success'
        });
        loadAssets();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || 'Erro ao excluir ativo',
          severity: 'error'
        });
      }
    }
  };

  const handleSelectAsset = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectAll = () => {
    const currentPageAssets = getCurrentPageAssets();
    const allSelected = currentPageAssets.every(asset => selectedAssets.includes(asset.id));

    if (allSelected) {
      setSelectedAssets(prev => prev.filter(id => !currentPageAssets.some(asset => asset.id === id)));
    } else {
      setSelectedAssets(prev => [
        ...prev,
        ...currentPageAssets.map(asset => asset.id).filter(id => !prev.includes(id))
      ]);
    }
  };

  const getCurrentPageAssets = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAssets.slice(startIndex, endIndex);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSubcategoryFilter('all');
    setLocationFilter('');
    setOwnerFilter('');
    setSelectedAssets([]);
  };

  const getUniqueValues = (field) => {
    return [...new Set(assets.map(asset => asset[field]).filter(Boolean))];
  };

  // Loading state
  if (loading) {
  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%',
        textAlign: 'center'
      }}>
        <Typography variant="h4" gutterBottom>
          Lista de Ativos
        </Typography>
        <LinearProgress />
      </Box>
    </Layout>
  );
  }

  const currentPageAssets = getCurrentPageAssets();
  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Cabeçalho Melhorado */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: `${categoryInfo.color}.main`, mr: 2 }}>
              {categoryInfo.icon === 'computer' && <ComputerIcon />}
              {categoryInfo.icon === 'key' && <KeyIcon />}
              {categoryInfo.icon === 'assignment' && <AssignmentIcon />}
            </Avatar>
            <Box>
              <Typography variant="h4">
                {categoryInfo.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredAssets.length} ativos encontrados • Página {currentPage} de {totalPages}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => loadAssets(true)}
              disabled={refreshing}
            >
              {refreshing ? 'Atualizando...' : 'Atualizar'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                const params = new URLSearchParams();
                if (category === 'physical' && subcategoryFilter !== 'all') {
                  params.set('subcategory', subcategoryFilter);
                }
                navigate(`/assets/${category}/new${params.toString() ? '?' + params.toString() : ''}`);
              }}
            >
              Novo Ativo
            </Button>
          </Box>
        </Box>

        {/* Filtros e Busca Avançados */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Filtros e Busca</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<FilterAltIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'contained' : 'outlined'}
                size="small"
              >
                Filtros
              </Button>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                size="small"
                disabled={!searchTerm && statusFilter === 'all' && !locationFilter && !ownerFilter}
              >
                Limpar
              </Button>
            </Box>
          </Box>

          {/* Filtros básicos (sempre visíveis) */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar por nome, descrição, responsável, localização, serial..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Todos os Status</MenuItem>
                  {Object.entries(ASSET_STATUSES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Chip label={config.label} color={config.color} size="small" sx={{ mr: 1 }} />
                      {config.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('cards')}
                  startIcon={<ViewModuleIcon />}
                  size="small"
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => setViewMode('table')}
                  startIcon={<ViewListIcon />}
                  size="small"
                >
                  Tabela
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Filtros avançados (expansíveis) */}
          <Collapse in={showFilters}>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Filtrar por Localização"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  placeholder="Digite uma localização..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Filtrar por Responsável"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  placeholder="Digite um responsável..."
                />
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Resultados e Controles */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedAssets.length > 0 && `${selectedAssets.length} selecionado(s) • `}
            Mostrando {currentPageAssets.length} de {filteredAssets.length} ativos
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2">Ordenar por:</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <MenuItem value="name">Nome</MenuItem>
                <MenuItem value="status">Status</MenuItem>
                <MenuItem value="value">Valor</MenuItem>
                <MenuItem value="created_at">Data de Criação</MenuItem>
                <MenuItem value="owner">Responsável</MenuItem>
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              <SortIcon sx={{ transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Lista de Ativos */}
        {viewMode === 'cards' ? (
          <Grid container spacing={3}>
            {currentPageAssets.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
                <AssetCard
                  asset={asset}
                  onEdit={() => handleEdit(asset.id)}
                  onDelete={() => handleDelete(asset.id)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={currentPageAssets.length > 0 && currentPageAssets.every(asset => selectedAssets.includes(asset.id))}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subcategoria</TableCell>
                  <TableCell>Quantidade</TableCell>
                  <TableCell>Em Estoque</TableCell>
                  <TableCell>Com Usuário</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Localização</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageAssets.map((asset) => (
                  <TableRow key={asset.id} hover selected={selectedAssets.includes(asset.id)}>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedAssets.includes(asset.id)}
                        onChange={() => handleSelectAsset(asset.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: `${categoryInfo.color}.light`, color: `${categoryInfo.color}.main`, mr: 2, width: 24, height: 24 }}>
                          {categoryInfo.icon === 'computer' && <ComputerIcon />}
                          {categoryInfo.icon === 'key' && <KeyIcon />}
                          {categoryInfo.icon === 'assignment' && <AssignmentIcon />}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">{asset.name}</Typography>
                        <ImportedSourceData product={asset} />
                          {asset.serial_number && (
                            <Typography variant="caption" color="text.secondary">
                              SN: {asset.serial_number}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ASSET_STATUSES[asset.status]?.label || asset.status}
                        color={ASSET_STATUSES[asset.status]?.color || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{asset.subcategory || '-'}</TableCell>
                    <TableCell>{asset.quantity ?? 1}</TableCell>
                    <TableCell>
                      {asset.category === 'physical' ? (
                        <Chip
                          label={asset.in_stock ? 'Sim' : 'Não'}
                          size="small"
                          color={asset.in_stock ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {asset.category === 'physical' ? (
                        <Chip
                          label={asset.with_user ? 'Sim' : 'Não'}
                          size="small"
                          color={asset.with_user ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>{asset.owner || '-'}</TableCell>
                    <TableCell>{asset.location || '-'}</TableCell>
                    <TableCell>
                      {asset.value ? `R$ ${asset.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(asset.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(asset.id)} disabled={asset.can_edit === false}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(asset.id)} color="error" disabled={asset.can_edit === false}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Estados vazios */}
        {filteredAssets.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum ativo encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {assets.length === 0
                ? 'Comece criando seu primeiro ativo'
                : 'Tente ajustar os filtros de busca ou limpe os filtros aplicados'
              }
            </Typography>
            {(searchTerm || statusFilter !== 'all' || locationFilter || ownerFilter) && (
              <Button startIcon={<ClearIcon />} onClick={clearAllFilters}>
                Limpar Filtros
              </Button>
            )}
          </Paper>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}

        {/* Snackbar melhorado */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default AssetList;
